import * as React from 'react';
import { useSelector } from 'react-redux';
import { getTranslate } from 'react-localize-redux';

import {
    makeStyles,
    Typography,
} from '@material-ui/core';

import { useParams } from 'react-router';
import classnames from 'classnames';
import { contentWidth } from '../../common/components/Layout';
import { PageLayout } from '../../common/components';
import { getBreadCrumb } from '../../common/breadcrumbUtil';
import { patientProgramElementGroups } from '../../common/constants';
import TaskElement from '../../youwell-common/components/taskElements/TaskElement';

const useStyles = makeStyles(theme => ({
    root: {
        marginTop: theme.spacing(4),
    },
    element: {
        marginBottom: theme.spacing(4),
    },
    elementHeading: {
        borderBottom: theme.youwell.borders.normal,
        borderBottomColor: theme.palette.secondary.main,
        borderBottomWidth: 4,
        display: 'flex',
        alignItems: 'center',
    },
    elementHeadingTitle: {
        flex: '1 1 auto',
    },
    items: {
        display: 'flex',
        flexDirection: 'column',

        '&.et_BullsEye': {
            flexDirection: 'row',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            maxWidth: contentWidth,

            [theme.breakpoints.down('sm')]: {
                justifyContent: 'space-evenly',
            },
            [theme.breakpoints.down('xs')]: {
                justifyContent: 'center',
            },
        },
    },
    item: {
        margin: theme.spacing(2, 0),
    },
    elementTaskTitle: {
        marginBottom: theme.spacing(1),
    },
    noElements: {
        margin: theme.spacing(2, 0),
    },
}), { name: 'ElementSummary' });

const getItems = (task, elementType) => task.contentElements
    ?.filter(d => (elementType ? d.elementType === elementType : d.questions?.length))
    .map(d => ({ key: `${task.id}-${d.id}`, element: d, task })) || [];

const getElements = (element, elementType) => {
    if (element) {
        if (element.task) {
            return getItems(element.task, elementType);
        }
        if (element.module?.tasks) {
            return element.module.tasks.reduce((prev, curr) => [
                ...prev, ...getItems(curr, elementType)], []);
        }
    }
    return [];
};

const renderElements = (element, elementType, classes, translate, allTaskResponsesForElement) => {
    const list = getElements(element, elementType);

    if (!list.length) {
        return (
            <Typography color="textSecondary" className={classes.noElements}>
                {translate('elementsPage.noItems')}
            </Typography>
        );
    }

    return list.map((item, i) => (
        <div key={item.key} className={classes.item}>
            <Typography variant="subtitle1" color="primary" className={classes.elementTaskTitle}>
                {item.task.name}
            </Typography>

            <TaskElement
                task={item.task}
                getProgramItem={() => {}}
                getSpecificTaskResponses={() => {}}
                element={item.element}
                taskResponses={allTaskResponsesForElement[item.task.id]}
                TaskFormProps={{ readOnly: true, small: true }}
                // LinkElementProps={LinkElementProps}
                elementIndex={i}
                onResponse={() => {}}
            />
        </div>
    ));
};

const ElementSummary = () => {
    const classes = useStyles();
    const { elementType } = useParams();
    const translate = useSelector(state => getTranslate(state.localize));
    const clientConfig = useSelector(state => state.auth.clientConfig);
    const currentProgram = useSelector(state => state.programStore.currentProgram);
    const allTaskResponses = useSelector(state => state.programStore.currentProgramTaskResponses || {});

    if (!currentProgram) {
        return null;
    }

    const mainElements = (currentProgram.groupedElements && currentProgram.groupedElements[patientProgramElementGroups.main]) || [];

    return (
        <PageLayout
            breadcrumb={getBreadCrumb(translate('elementsPage.heading'), translate, clientConfig)}
        >
            <div className={classes.root}>
                {mainElements.map(element => (
                    <div className={classes.element} key={element.module ? element.module.id : element.task && element.task.id}>

                        <div className={classes.elementHeading}>
                            <Typography variant="subtitle1" color="inherit" className={classes.elementHeadingTitle}>
                                {element.name}
                            </Typography>
                        </div>

                        <div className={classnames(classes.items, `et_${elementType}`)}>
                            {renderElements(element, elementType, classes, translate, allTaskResponses[element.id] || {})}
                        </div>
                    </div>
                ))}
            </div>
        </PageLayout>
    );
};

export default ElementSummary;
